/**
 * Prévisualisation d'une entrée d'aliment — **jamais une source de vérité**.
 *
 * ⚠️ Le §5.1 est sans ambiguïté : « le serveur recalcule toujours les macros et
 * ignore les valeurs envoyées par le client. Le client ne fait qu'afficher une
 * prévisualisation. » Ce fichier est exactement cette prévisualisation, et rien
 * d'autre :
 *
 *   - ce qu'il produit **n'est jamais envoyé** au serveur — le protocole
 *     n'accepte aucune macro pour une entrée de type `food` (cf.
 *     `foodEntryInput` dans `routes/days.ts`, qui la refuse en 400) ;
 *   - il sert à deux choses seulement : afficher le total avant validation, et
 *     alimenter la mise à jour optimiste du bandeau de restant (§6.2), écrasée
 *     par la réponse du serveur dès qu'elle arrive.
 *
 * ⚠️ **Ces calculs dupliquent volontairement `resolveFood()` côté serveur.**
 * C'est un doublon assumé, et le seul du projet : sans lui, il n'y aurait pas de
 * mise à jour optimiste possible pour un aliment, donc pas de bandeau qui bouge
 * tout de suite — c'est-à-dire pas de cible des 15 secondes (specs §4). Si les
 * deux divergeaient, le serveur gagnerait immédiatement et sans dégât : l'écart
 * se verrait le temps d'un aller-retour, puis disparaîtrait.
 *
 * ⚠️ Les fonctions prennent des valeurs nues plutôt qu'un `FoodDTO` : le même
 * calcul sert à un aliment de la base **et** à un produit Open Food Facts pas
 * encore importé, dont les macros sont en cours d'édition à l'écran (specs §6.4).
 */

import type { BaseUnit, MacroSet } from '../../../shared/types';

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Quantité ramenée en unité de base, quel que soit le mode de saisie.
 * Sans portion, la quantité est déjà en g/ml ; avec, c'est un nombre de portions.
 */
export function baseQuantityOf(quantity: number, portionQuantity?: number): number {
  const safe = Math.max(0, quantity);
  return portionQuantity === undefined ? safe : safe * portionQuantity;
}

/** Macros d'une quantité donnée. Miroir de `resolveFood()` — cf. l'avertissement. */
export function previewMacrosFor(per100: MacroSet, baseQuantity: number): MacroSet {
  const factor = Math.max(0, baseQuantity) / 100;
  return {
    protein: round2(per100.protein * factor),
    carbs: round2(per100.carbs * factor),
    fat: round2(per100.fat * factor),
    kcal: round2(per100.kcal * factor),
  };
}

/**
 * Libellé que le serveur figera pour une entrée d'aliment.
 *
 * Reproduit `composeLabel()` de `services/macros.ts` au caractère près : si
 * l'aperçu affichait « Skyr » et la réponse « Skyr — Danone », la ligne
 * changerait de texte sous les yeux de l'utilisateur au moment où le serveur
 * répond.
 */
export function composeFoodLabel(name: string, brand: string | null): string {
  const composed = brand === null || brand.trim() === '' ? name : `${name} — ${brand}`;
  return composed.length <= 100 ? composed : composed.slice(0, 100);
}

/** Libellé de portion figé par le serveur : « 1 tranche (32 g) ». */
export function composePortionLabel(
  label: string,
  quantity: number,
  baseUnit: BaseUnit,
): string {
  return `${label} (${String(round2(quantity))} ${baseUnit})`;
}
